import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Add, Delete } from '@mui/icons-material';

import { get } from '@/api/httpClient';
import {
  type FlowgateColumn,
  type FlowgateDomainFilters,
  type FlowgateFilter,
  type FlowgateFilterOp,
} from '@/api/flowgate';
import {
  type GenePanelCartPayload,
  type GenePanelResultSchema,
} from '@/zod_schemas';

// The value the panel lifts up on Apply: gene-aware domain filters (resolved
// server-side) plus generic per-column predicates (AND-combined on the BE).
export type FlowgateFilterValue = {
  domainFilters: FlowgateDomainFilters;
  filters: FlowgateFilter[];
};

type ColumnPredicate = {
  id: string;
  column: string;
  op: FlowgateFilterOp;
  value: string;
};

const EXOMISER_RANK_DEFAULT = 10;
const EXOMISER_RANK_RE = /^exomi[sz]er_rank$/i;

const PREDICATE_OPS: { value: FlowgateFilterOp; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'ne', label: '≠' },
  { value: 'lt', label: '<' },
  { value: 'le', label: '≤' },
  { value: 'gt', label: '>' },
  { value: 'ge', label: '≥' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
];

// Split a comma/space/newline separated string into trimmed non-empty tokens.
const tokenize = (raw: string): string[] =>
  raw
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter(Boolean);

// One HPO term as selected in the autocomplete. `hpoId` is the HP:xxxxxxx id
// sent to the BE; `name` is for display only.
type HpoOption = { hpoId: string; name: string };

// JAX HPO ontology term search (global, public). Bare axios (no app cookies /
// baseURL) since it's a third-party host.
type JaxTerm = { id: string; name: string };
const searchHpoTerms = async (query: string): Promise<HpoOption[]> => {
  const resp = await axios.get<{ terms?: JaxTerm[] }>(
    `https://ontology.jax.org/api/hp/search?q=${encodeURIComponent(
      query,
    )}&limit=20`,
  );
  return (resp.data.terms ?? []).map((t) => ({ hpoId: t.id, name: t.name }));
};

// Genes associated with each selected HPO term, from the same JAX annotation
// endpoint the backend uses to populate its HPO→gene table. Returns one group
// per term (term id/name + its sorted gene symbols) so the panel can show the
// per-term breakdown.
type JaxGene = { id: string; name: string };
type HpoGeneGroup = { hpoId: string; name: string; genes: string[] };
const fetchGenesPerHpo = async (terms: HpoOption[]): Promise<HpoGeneGroup[]> =>
  Promise.all(
    terms.map(async (t) => {
      const resp = await axios.get<{ genes?: JaxGene[] }>(
        `https://ontology.jax.org/api/network/annotation/${encodeURIComponent(
          t.hpoId,
        )}`,
      );
      const genes = Array.from(
        new Set((resp.data.genes ?? []).map((g) => g.name)),
      ).sort();
      return { hpoId: t.hpoId, name: t.name, genes };
    }),
  );

type Props = {
  columns: FlowgateColumn[];
  onApply: (value: FlowgateFilterValue) => void;
  onClear: () => void;
};

const FlowgateFilterPanel = ({ columns, onApply, onClear }: Props) => {
  const [open, setOpen] = useState(true);

  // HPO: autocomplete-selected terms + debounced search text.
  const [hpoSelected, setHpoSelected] = useState<HpoOption[]>([]);
  const [hpoInput, setHpoInput] = useState('');
  const [hpoQuery, setHpoQuery] = useState('');
  const [geneIncludeInput, setGeneIncludeInput] = useState('');
  const [geneInclude, setGeneInclude] = useState<string[]>([]);
  const [geneExcludeInput, setGeneExcludeInput] = useState('');
  const [geneExclude, setGeneExclude] = useState<string[]>([]);
  const [panelIds, setPanelIds] = useState<string[]>([]);
  const [predicates, setPredicates] = useState<ColumnPredicate[]>([]);
  // Opt-in. Off → other filters cover every row. On → only ranks 1..N.
  const [useExomiserRank, setUseExomiserRank] = useState(false);
  const [exomiserTop, setExomiserTop] = useState(String(EXOMISER_RANK_DEFAULT));

  const exomiserColumn = columns.find((c) => EXOMISER_RANK_RE.test(c.name));
  const parsedExomiserTop = Number.parseInt(exomiserTop, 10);
  const exomiserLimit =
    Number.isFinite(parsedExomiserTop) && parsedExomiserTop >= 1
      ? parsedExomiserTop
      : EXOMISER_RANK_DEFAULT;

  // Gene panels are a global resource (GET /gene_panels/). Plain useQuery (not
  // the Suspense variant) so this panel needs no Suspense boundary.
  const { data: genePanels } = useQuery<GenePanelCartPayload[]>({
    queryKey: ['gene_panels', 'list'],
    queryFn: async () => {
      const resp = await get<GenePanelResultSchema>('/gene_panels/');
      return resp.data.results;
    },
  });

  // Debounce the HPO search box so we don't hit the ontology API per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setHpoQuery(hpoInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [hpoInput]);

  const { data: hpoOptions = [], isFetching: hpoLoading } = useQuery<
    HpoOption[]
  >({
    queryKey: ['hpo-search', hpoQuery],
    enabled: hpoQuery.length >= 2,
    queryFn: () => searchHpoTerms(hpoQuery),
  });

  // Genes covered by each selected HPO term (so the user can see, per term,
  // what the HPO filter expands to).
  const [showHpoGenes, setShowHpoGenes] = useState(false);
  const hpoIds = hpoSelected.map((o) => o.hpoId);
  const { data: hpoGeneGroups = [], isFetching: hpoGenesLoading } = useQuery<
    HpoGeneGroup[]
  >({
    queryKey: ['hpo-genes', [...hpoIds].sort()],
    enabled: hpoSelected.length > 0,
    queryFn: () => fetchGenesPerHpo(hpoSelected),
  });
  // Union count across all selected terms, for the summary label.
  const hpoGeneTotal = new Set(hpoGeneGroups.flatMap((g) => g.genes)).size;

  const addTokens = (
    raw: string,
    current: string[],
    setChips: (v: string[]) => void,
    setInput: (v: string) => void,
  ) => {
    const next = Array.from(new Set([...current, ...tokenize(raw)]));
    setChips(next);
    setInput('');
  };

  // Merge a chip array with whatever is still uncommitted in its input box, so
  // text the user typed but did not press Enter on is not silently dropped.
  const merge = (chips: string[], pending: string): string[] =>
    Array.from(new Set([...chips, ...tokenize(pending)]));

  const buildValue = (
    hpo: string[],
    include: string[],
    exclude: string[],
    exomiserRank: number | null,
  ): FlowgateFilterValue => {
    const domainFilters: FlowgateDomainFilters = {};
    if (hpo.length) domainFilters.hpo_filters = hpo;
    if (panelIds.length) domainFilters.gene_panel_ids = panelIds;
    if (include.length || exclude.length) {
      domainFilters.gene_filters = {
        ...(include.length ? { include } : {}),
        ...(exclude.length ? { exclude } : {}),
      };
    }
    if (exomiserRank != null) domainFilters.exomiser_rank = exomiserRank;
    const filters: FlowgateFilter[] = predicates
      .filter((p) => p.column && p.value !== '')
      .map(
        (p) =>
          ({ column: p.column, op: p.op, value: p.value }) as FlowgateFilter,
      );
    return { domainFilters, filters };
  };

  const handleApply = () => {
    // HPO comes from the autocomplete selection; gene boxes flush any pending
    // (typed-but-not-Enter) text first so it isn't silently dropped.
    const hpo = hpoSelected.map((o) => o.hpoId);
    const typedInclude = merge(geneInclude, geneIncludeInput);
    const exclude = merge(geneExclude, geneExcludeInput);
    setGeneInclude(typedInclude);
    setGeneIncludeInput('');
    setGeneExclude(exclude);
    setGeneExcludeInput('');

    // The BE resolves HPO -> genes from its HpoGene table, which is incomplete
    // for many terms (→ 0 genes → no filtering, showing all rows). We already
    // fetched each selected term's genes from the JAX ontology for display
    // ("N genes associated"), so send them as `include` too. The BE unions
    // HpoGene(hpo) with include, so the HPO filter uses exactly the genes shown
    // and works for every term regardless of the DB mapping.
    const hpoGenes = hpoGeneGroups.flatMap((g) => g.genes);
    const include = Array.from(new Set([...typedInclude, ...hpoGenes]));

    const exomiserRank =
      useExomiserRank && exomiserColumn ? exomiserLimit : null;

    onApply(buildValue(hpo, include, exclude, exomiserRank));
  };

  const handleClear = () => {
    setHpoSelected([]);
    setHpoInput('');
    setHpoQuery('');
    setGeneInclude([]);
    setGeneIncludeInput('');
    setGeneExclude([]);
    setGeneExcludeInput('');
    setPanelIds([]);
    setPredicates([]);
    setUseExomiserRank(false);
    setExomiserTop(String(EXOMISER_RANK_DEFAULT));
    onClear();
  };

  if (!open) {
    return (
      <Box
        component="button"
        type="button"
        aria-label="Show filters"
        onClick={() => setOpen(true)}
        sx={{
          width: { xs: '100%', lg: 44 },
          flexShrink: 0,
          alignSelf: { lg: 'stretch' },
          m: 0,
          p: 0,
          py: 1.5,
          px: { xs: 2, lg: 0 },
          border: '1px solid',
          borderColor: 'primary.light',
          borderRadius: 1.5,
          bgcolor: 'primary.light',
          color: 'primary.main',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: { xs: 'row', lg: 'column' },
          alignItems: 'center',
          justifyContent: { xs: 'center', lg: 'flex-start' },
          gap: 1,
          font: 'inherit',
          '&:hover': {
            bgcolor: 'primary.main',
            borderColor: 'primary.main',
            color: 'primary.contrastText',
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }}
      >
        <ChevronRight fontSize="small" />
        <Typography
          component="span"
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            writingMode: { lg: 'vertical-rl' },
            transform: { lg: 'rotate(180deg)' },
          }}
        >
          Filters
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: { xs: '100%', lg: 360 },
        flexShrink: { xs: 1, lg: 0 },
        minHeight: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        // Beside the grid the panel can use the full height. Stacked above it,
        // cap the panel so the table keeps the rest of the screen and scrolls
        // on its own.
        maxHeight: { xs: '42%', lg: '100%' },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          minHeight: 58,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Box>
          <Typography variant="subtitle1">Filters</Typography>
          <Typography variant="caption" color="text.secondary">
            Genes, rank, and fields
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={() => setOpen(false)}
          startIcon={<ChevronLeft />}
          sx={{
            color: 'text.secondary',
            minWidth: 0,
            '&:hover': {
              color: 'primary.main',
              bgcolor: 'primary.light',
            },
          }}
        >
          Hide
        </Button>
      </Box>

      <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
        <Stack spacing={2.5}>
          {exomiserColumn && (
            <Box>
              <FormControlLabel
                labelPlacement="start"
                sx={{
                  ml: 0,
                  mr: 0,
                  width: '100%',
                  justifyContent: 'space-between',
                }}
                control={
                  <Switch
                    size="small"
                    checked={useExomiserRank}
                    onChange={(e) => setUseExomiserRank(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="subtitle2">Exomiser rank</Typography>
                }
              />
              {useExomiserRank && (
                <TextField
                  size="small"
                  type="number"
                  fullWidth
                  label="Top ranking"
                  value={exomiserTop}
                  onChange={(e) => setExomiserTop(e.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                  helperText={`Ranks 1–${exomiserLimit}`}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          )}

          {/* HPO terms and Gene panels hidden. Remove `false &&` to restore. */}
          {false && (
            <>
              {/* HPO terms — autocomplete against the JAX ontology */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  HPO terms
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={hpoOptions}
                  value={hpoSelected}
                  loading={hpoLoading}
                  // Server-side search: don't filter the fetched options locally.
                  filterOptions={(opts) => opts}
                  getOptionLabel={(o) => `${o.hpoId} — ${o.name}`}
                  isOptionEqualToValue={(o, v) => o.hpoId === v.hpoId}
                  noOptionsText={
                    hpoQuery.length < 2 ? 'Type to search…' : 'No matches'
                  }
                  onInputChange={(_e, v) => setHpoInput(v)}
                  onChange={(_e, vals) => {
                    // Dedupe selected terms by HP id.
                    const seen = new Set<string>();
                    setHpoSelected(
                      vals.filter((o) => {
                        if (seen.has(o.hpoId)) return false;
                        seen.add(o.hpoId);
                        return true;
                      }),
                    );
                  }}
                  renderOption={(props, o) => (
                    <li {...props} key={o.hpoId}>
                      <Box>
                        <Typography variant="body2">{o.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {o.hpoId}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search HPO (e.g. seizure)"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {hpoLoading ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {/* Genes covered by the selected HPO terms, grouped per term */}
                {hpoIds.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {hpoGenesLoading ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={14} />
                        <Typography variant="caption" color="text.secondary">
                          Loading associated genes…
                        </Typography>
                      </Stack>
                    ) : (
                      <>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setShowHpoGenes((s) => !s)}
                          sx={{
                            textTransform: 'none',
                            p: 0,
                            minWidth: 0,
                            fontWeight: 400,
                          }}
                        >
                          {hpoGeneTotal} gene
                          {hpoGeneTotal === 1 ? '' : 's'} associated
                          {showHpoGenes ? ' ▲' : ' ▼'}
                        </Button>
                        <Collapse in={showHpoGenes} unmountOnExit>
                          <Stack
                            spacing={1}
                            sx={{ mt: 0.5, maxHeight: 220, overflowY: 'auto' }}
                          >
                            {hpoGeneGroups.map((group) => (
                              <Box key={group.hpoId}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 600, display: 'block' }}
                                >
                                  {group.name} ({group.genes.length})
                                </Typography>
                                <Box
                                  sx={{
                                    mt: 0.25,
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                  }}
                                >
                                  {group.genes.length === 0 ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      No genes mapped.
                                    </Typography>
                                  ) : (
                                    group.genes.map((g) => (
                                      <Chip
                                        key={`${group.hpoId}-${g}`}
                                        label={g}
                                        size="small"
                                      />
                                    ))
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </Collapse>
                      </>
                    )}
                  </Box>
                )}
              </Box>

              {/* Gene panels */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Gene panels
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={genePanels ?? []}
                  getOptionLabel={(o) => o.name}
                  value={(genePanels ?? []).filter(
                    (p) => p.id && panelIds.includes(p.id),
                  )}
                  onChange={(_e, selected) =>
                    setPanelIds(
                      selected
                        .map((p) => p.id)
                        .filter((id): id is string => !!id),
                    )
                  }
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select panels" />
                  )}
                />
              </Box>
            </>
          )}

          {/* Gene include / exclude */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Genes (include)
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="BRCA1 TP53 … (Enter to add)"
              value={geneIncludeInput}
              onChange={(e) => setGeneIncludeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTokens(
                    geneIncludeInput,
                    geneInclude,
                    setGeneInclude,
                    setGeneIncludeInput,
                  );
                }
              }}
            />
            {geneInclude.length > 0 && (
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                sx={{ mt: 1 }}
              >
                {geneInclude.map((g) => (
                  <Chip
                    key={g}
                    label={g}
                    size="small"
                    color="success"
                    onDelete={() =>
                      setGeneInclude(geneInclude.filter((x) => x !== g))
                    }
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Genes (exclude)
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Genes to exclude (Enter to add)"
              value={geneExcludeInput}
              onChange={(e) => setGeneExcludeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTokens(
                    geneExcludeInput,
                    geneExclude,
                    setGeneExclude,
                    setGeneExcludeInput,
                  );
                }
              }}
            />
            {geneExclude.length > 0 && (
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                sx={{ mt: 1 }}
              >
                {geneExclude.map((g) => (
                  <Chip
                    key={g}
                    label={g}
                    size="small"
                    color="error"
                    onDelete={() =>
                      setGeneExclude(geneExclude.filter((x) => x !== g))
                    }
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <Divider />

          {/* Column predicates */}
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle2">Column filters</Typography>
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() =>
                  setPredicates((prev) => [
                    ...prev,
                    {
                      id: `${prev.length}-${columns[0]?.name ?? ''}`,
                      column: columns[0]?.name ?? '',
                      op: 'eq',
                      value: '',
                    },
                  ])
                }
              >
                Add
              </Button>
            </Stack>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {predicates.map((p, idx) => (
                <Stack
                  key={p.id}
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                >
                  <Select
                    size="small"
                    value={p.column}
                    onChange={(e) =>
                      setPredicates((prev) =>
                        prev.map((x, i) =>
                          i === idx ? { ...x, column: e.target.value } : x,
                        ),
                      )
                    }
                    sx={{ minWidth: 110 }}
                  >
                    {columns.map((c) => (
                      <MenuItem key={c.name} value={c.name}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Select
                    size="small"
                    value={p.op}
                    onChange={(e) =>
                      setPredicates((prev) =>
                        prev.map((x, i) =>
                          i === idx
                            ? { ...x, op: e.target.value as FlowgateFilterOp }
                            : x,
                        ),
                      )
                    }
                    sx={{ width: 90 }}
                  >
                    {PREDICATE_OPS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <TextField
                    size="small"
                    value={p.value}
                    onChange={(e) =>
                      setPredicates((prev) =>
                        prev.map((x, i) =>
                          i === idx ? { ...x, value: e.target.value } : x,
                        ),
                      )
                    }
                    sx={{ flexGrow: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() =>
                      setPredicates((prev) => prev.filter((_x, i) => i !== idx))
                    }
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button variant="contained" fullWidth onClick={handleApply}>
                Apply
              </Button>
              <Button variant="outlined" onClick={handleClear}>
                Clear
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default FlowgateFilterPanel;
